import {AboutUsPageComponent} from "./about-us-page/about-us-page.component";
import {CommonQuestionsPageComponent} from "./common-questions-page/common-questions-page.component";
import {PrivacyPolicyPageComponent} from "./privacy-policy-page/privacy-policy-page.component";
import {HowItWorksComponent} from "./how-it-works/how-it-works.component";
import {TermsAndConditionsPageComponent} from "./terms-and-conditions-page/terms-and-conditions-page.component";
import {YourDataPageComponent} from "./your-data-page/your-data-page.component";
import {ContactPageComponent} from "./contact-page/contact-page.component";
import {HomeyRoute} from "../../app.routes";
import {ArticlesOverviewPageComponent} from "./articles-overview-page/articles-overview-page.component";
import {ArticlePageComponent} from "./article-page/article-page.component";
import {inject} from "@angular/core";
import {ArticleService} from "../../services/article.service";
import {CanMatchFn, UrlSegment} from "@angular/router";
import {environment} from "../../../../environments/environment";
import {of} from "rxjs";

/**
 * Extract a specific parameter (like :slug) from segments based on the route definition
 */
function extractParamFromSegments(routePath: string | undefined, segments: UrlSegment[], paramName: string): string | undefined {
  if (!routePath) return undefined;
  const parts = routePath.split("/");
  const paramIndex = parts.findIndex(p => p === `:${paramName}`);
  if (paramIndex < 0 || paramIndex >= segments.length) return undefined;
  return segments[paramIndex].path;
}

/**
 * Clean CanMatchFn that checks if an article exists without relying on the last segment hack
 */
const articleExists: CanMatchFn = (route, segments) => {
  const slug = extractParamFromSegments(route.path, segments, "slug");
  return slug ? inject(ArticleService).hasArticle(slug) : of(false);
};

function breadcrumbGraph(path: string, name: string) {
  return [
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", position: 1, name: "Home", item: environment.baseUrl },
        { "@type": "ListItem", position: 2, name, item: `${environment.baseUrl}/${path}` },
      ],
    },
  ];
}

function articleStructuredData(slug: string, langPrefix = "") {
  const article = inject(ArticleService).getArticle(slug);
  if (!article) return undefined;

  const prefix = langPrefix ? `/${langPrefix}` : "";

  return of({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", position: 1, name: "Home", item: environment.baseUrl },
          { "@type": "ListItem", position: 2, name: "Articles", item: `${environment.baseUrl}${prefix}/articles` },
          { "@type": "ListItem", position: 3, name: article.title, item: `${environment.baseUrl}${prefix}/articles/${slug}` },
        ],
      },
      {
        "@type": "Article",
        "mainEntityOfPage": { "@type": "WebPage", "@id": `${environment.baseUrl}${prefix}/articles/${slug}` },
        headline: article.title,
        image: [article.thumbnail],
        author: { "@type": "Organization", name: "Homey NYC", url: environment.baseUrl },
        datePublished: `${article.published}T08:00:00+02:00`,
        dateModified: `${article.published}T08:00:00+02:00`,
      },
    ],
  });
}

function articleRoutes(langPrefix = ""): HomeyRoute {
  const prefix = langPrefix ? `${langPrefix}/` : "";
  const isSpanish = langPrefix === "es";

  return {
    path: `${langPrefix ? langPrefix + '/' : ''}articles`,
    data: {
      index: true,
      headerStyle: "landing-sub-page",
      language: isSpanish ? 'es' : 'en',
      canTranslate: true,
      metaDescription: isSpanish
        ? "Descubre las últimas noticias sobre la Lotería de Vivienda de Nueva York y la vivienda asequible. Aprende cómo Homey simplifica el proceso para ayudarte a encontrar vivienda asequible sin estrés."
        : "Have questions about your NYC Housing Lottery application by HPD? Need assistance with affordable housing or low income housing? Contact our support team—we’re here to help!",
    },
    children: [
      {
        path: "",
        pathMatch: "full",
        component: ArticlesOverviewPageComponent,
        title: isSpanish
          ? "Noticias sobre la Lotería de Vivienda de NYC y Vivienda Asequible - Homey, la Lotería de Vivienda Hecha Fácil"
          : "NYC Housing Lottery & Affordable Housing News - Homey Housing Lottery Made Easy",
        data: {
          structuredData: () =>
            of({
              "@context": "https://schema.org",
              "@graph": breadcrumbGraph(`${prefix}articles`, isSpanish ? "Artículos" : "Articles"),
            }),
        },
      },
      {
        path: ":slug",
        component: ArticlePageComponent,
        canMatch: [articleExists],
        title: (route) => inject(ArticleService).getArticleTitle(route.paramMap.get("slug")),
        data: {
          metaDescription: (route) => inject(ArticleService).getArticleDescription(route.paramMap.get("slug")),
          structuredData: (params) => {
            const slug = params.get("slug");
            return slug ? articleStructuredData(slug, langPrefix) : undefined;
          },
        },
      },
    ],
  };
}

export const extraPages: HomeyRoute[] = [
  {
    path: "about-us",
    component: AboutUsPageComponent,
    title:
      "About Homey NYC, Housing Lottery Made Easy - Our Mission to Simplify Affordable Housing in NYC",
    data: {
      index: true,
      headerStyle: "landing-sub-page",
      metaDescription:
        "At Homey, we're committed to simplifying NYC's housing lottery process. Learn how our automated platform helps New Yorkers find affordable housing hassle-free."
    }
  },
  {
    path: "common-questions",
    component: CommonQuestionsPageComponent,
    title:
      "Homey FAQs – NYC Housing Lottery & Affordable Housing Questions Answered",
    data: {
      index: true,
      headerStyle: "landing-sub-page",
      metaDescription:
        "Find answers to common questions about Homey, the NYC Housing Lottery, Housing Connect, HPD, and affordable housing applications. Learn how to apply and maximize your chances of winning!"
    }
  },
  {
    path: "privacy-policy",
    component: PrivacyPolicyPageComponent,
    data: {
      index: true,
      headerStyle: "landing-sub-page"
    }
  },
  {
    path: "how-it-works",
    component: HowItWorksComponent,
    title: "How Homey Works – NYC Housing Lottery Made Easy & Automated",
    data: {
      index: true,
      headerStyle: "landing-sub-page",
      metaDescription:
        "Learn how Homey automates your NYC Housing Lottery applications. We handle paperwork, apply on your behalf, and notify you when you win—stress-free affordable housing in NYC!"
    }
  },
  {
    path: "terms-and-conditions",
    component: TermsAndConditionsPageComponent,
    data: {
      index: true,
      headerStyle: "landing-sub-page"
    }
  },
  {
    path: "your-data",
    component: YourDataPageComponent,
    title:
      "Your Data & Privacy – How Homey Protects Your Information For The Housing Lottery",
    data: {
      index: true,
      headerStyle: "landing-sub-page",
      metaDescription:
        "Your privacy and security for affordable housing are our top priorities when applying you to housing connect. Homey collects only essential data for NYC Housing Lottery applications, never asks for SSNs, and encrypts all information. Learn how we keep your data safe for low income housing."
    }
  },
  {
    path: "contact",
    component: ContactPageComponent,
    title:
      "Contact Homey Housing Lottery Made Easy – Get Help with Your NYC Housing Lottery Application",
    data: {
      index: true,
      headerStyle: "landing-sub-page",
      metaDescription:
        "Have questions about your NYC Housing Lottery application by HPD? Need assistance with affordable housing or low income housing? Contact our support team—we’re here to help!"
    }
  },
  articleRoutes(),
  articleRoutes("es"),
];
